/**
 * Toast Notification Component - Apple-style success/error/warning messages
 */

import { useEffect, useState } from 'react';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose: () => void;
}

// Create a container for toasts if it doesn't exist
let toastContainer: HTMLDivElement | null = null;

// Function to ensure the toast container exists in the DOM
const ensureToastContainer = () => {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    // Apple-correct placement: top-center
    // z-[99999] ensures toasts are always above modals (which use z-9000+)
    toastContainer.className = `fixed top-5 left-1/2 -translate-x-1/2 z-[99999] flex flex-col gap-2`;
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
};

// Toast options interface
interface ToastOptions {
  action?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
}

// Create toast function
const createToast = (message: string, type: 'success' | 'error' | 'warning' | 'info', durationOrOptions: number | ToastOptions = 3000) => {
  // Parse options
  const options: ToastOptions = typeof durationOrOptions === 'number'
    ? { duration: durationOrOptions }
    : durationOrOptions;
  const duration = options.duration || 3000;
  const container = ensureToastContainer();
  const toastId = `toast-${Date.now()}`;
  const toastElement = document.createElement('div');
  toastElement.id = toastId;
  container.appendChild(toastElement);

  // Create toast container with Apple styling
  const toastContainer = document.createElement('div');
  toastContainer.setAttribute('role', 'status');
  toastContainer.setAttribute('aria-live', 'polite');
  toastContainer.className = `
    pointer-events-auto
    flex items-center gap-3
    rounded-[18px]
    px-5 py-3
    shadow-[0_12px_30px_rgba(0,0,0,0.10)]
    bg-white/75
    backdrop-blur-xl
    text-slate-900
    ring-1 ring-black/5
  `;
  
  // Add animation
  toastContainer.style.animation = 'toastIn 220ms cubic-bezier(.2,.8,.2,1)';

  // Icon container (no background — icon only)
  const iconContainer = document.createElement('div');
  iconContainer.className = 'h-5 w-5 grid place-items-center flex-shrink-0';
  
  // Create icon
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('fill', 'none');
  icon.setAttribute('stroke', 'currentColor');
  icon.setAttribute('stroke-width', '2.5');
  icon.setAttribute('stroke-linecap', 'round');
  icon.setAttribute('stroke-linejoin', 'round');
  icon.setAttribute('class', `h-4 w-4 ${
    type === 'success' ? 'text-emerald-600' :
    type === 'error' ? 'text-red-600' :
    type === 'warning' ? 'text-amber-600' :
    'text-blue-600'
  }`);
  
  if (type === 'success') {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20 6 9 17l-5-5');
    icon.appendChild(path);
  } else if (type === 'error') {
    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M18 6 6 18');
    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M6 6l12 12');
    icon.appendChild(path1);
    icon.appendChild(path2);
  } else if (type === 'warning') {
    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M12 9v4');
    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M12 17h.01');
    const path3 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path3.setAttribute('d', 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z');
    icon.appendChild(path1);
    icon.appendChild(path2);
    icon.appendChild(path3);
  } else {
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '10');
    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M12 16v-4');
    const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path2.setAttribute('d', 'M12 8h.01');
    icon.appendChild(circle);
    icon.appendChild(path1);
    icon.appendChild(path2);
  }
  
  iconContainer.appendChild(icon);

  // Message
  const messageDiv = document.createElement('div');
  messageDiv.className = 'text-[15px] font-medium leading-none tracking-[-0.01em]';
  messageDiv.textContent = message;
  
  // Build DOM
  toastContainer.appendChild(iconContainer);
  toastContainer.appendChild(messageDiv);
  
  const element = document.getElementById(toastId);
  if (element) {
    element.appendChild(toastContainer);
    
    // Auto-dismiss after duration — safe cleanup checks parent still exists
    const dismissTimer = setTimeout(() => {
      toastContainer.style.animation = 'toastOut 180ms cubic-bezier(.4,0,1,1)';
      const removeTimer = setTimeout(() => {
        try {
          if (element.parentNode) element.parentNode.removeChild(element);
        } catch { /* element already removed */ }
      }, 180);
      // Store for potential cleanup
      (element as HTMLElement & { _removeTimer?: ReturnType<typeof setTimeout> })._removeTimer = removeTimer;
    }, duration);
    // Store for potential cleanup
    (element as HTMLElement & { _dismissTimer?: ReturnType<typeof setTimeout> })._dismissTimer = dismissTimer;
  }
};

// Export toast functions
export const showToast = {
  success: (message: string, durationOrOptions: number | ToastOptions = 3000) => {
    createToast(message, 'success', durationOrOptions);
  },
  error: (message: string, durationOrOptions: number | ToastOptions = 3000) => {
    createToast(message, 'error', durationOrOptions);
  },
  warning: (message: string, durationOrOptions: number | ToastOptions = 3000) => {
    createToast(message, 'warning', durationOrOptions);
  },
  info: (message: string, durationOrOptions: number | ToastOptions = 3000) => {
    createToast(message, 'info', durationOrOptions);
  }
};

// React component version (Apple-styled)
export const Toast: React.FC<ToastProps> = ({ 
  message, 
  type = 'success', 
  duration = 3000,
  onClose 
}) => {
  const [visible, setVisible] = useState(true);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(onClose, 180);
    }, duration);
    
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 text-emerald-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        );
      case 'error':
        return (
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 text-red-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="M6 6l12 12" />
          </svg>
        );
      case 'warning':
        return (
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 text-amber-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          </svg>
        );
      default:
        return (
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 text-blue-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        );
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`
        pointer-events-auto
        flex items-center gap-3
        rounded-[18px]
        px-5 py-3
        shadow-[0_12px_30px_rgba(0,0,0,0.10)]
        bg-white/75
        backdrop-blur-xl
        text-slate-900
        ring-1 ring-black/5
      `}
      style={{
        animation: visible 
          ? 'toastIn 220ms cubic-bezier(.2,.8,.2,1)' 
          : 'toastOut 180ms cubic-bezier(.4,0,1,1)'
      }}
    >
      <div className="h-5 w-5 grid place-items-center flex-shrink-0">
        {getIcon()}
      </div>
      
      <div className="text-[15px] font-medium leading-none tracking-[-0.01em]">
        {message}
      </div>
    </div>
  );
};

export default Toast;
